import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router';
import dayjs from 'dayjs';
import _ from 'lodash';

import FixedLoader from 'components/FixedLoader/FixedLoader';
import ConfirmDialog from 'components/ConfirmDialog';
import { MembershipRestrictionModal, type MembershipDialogCopy } from 'components/Modal';
import { TopBarRedesigned } from 'components/TopBarRedesigned';
import MembershipBannerMessage from 'components/MembershipBannerMessage';
import { StyledButton } from 'components/Buttons/DefaultButton/DefaultButton.styles';
import Api from 'api';
import useActionApi from 'hooks/useActionApi';
import { usePersistedState } from 'hooks';
import entities from 'reduxStore/entityKeys';
import { COUNTRY } from 'utils/constants';
import { getPaymentTerms, getPaymentType } from 'utils';
import {
  canConfirmAutobookByMembership,
  getJobRequesterMembershipDecision,
  getMembershipBannerCopy,
  getMembershipRestrictionDialogCopy,
  openMembershipPath,
  shouldShowMembershipBanner,
} from 'utils/membership';
import { getTalentParkingLabel } from 'utils/parking';
import { JobProps, HygienistProps, MembershipStatusResponse } from 'utils/types';
import { ShiftNotesPanel } from '../ConfirmedOfferPage/components/ShiftNotesPanel';

import RoomOutlinedIcon from '@mui/icons-material/RoomOutlined';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import AttachMoneyOutlinedIcon from '@mui/icons-material/AttachMoneyOutlined';
import FlashOnOutlinedIcon from '@mui/icons-material/FlashOnOutlined';
import TimelapseOutlinedIcon from '@mui/icons-material/TimelapseOutlined';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import TimeToLeaveOutlinedIcon from '@mui/icons-material/TimeToLeaveOutlined';
import DiningOutlinedIcon from '@mui/icons-material/DiningOutlined';
import DesktopMacOutlinedIcon from '@mui/icons-material/DesktopMacOutlined';
import WavesOutlinedIcon from '@mui/icons-material/WavesOutlined';
import BlurLinearOutlinedIcon from '@mui/icons-material/BlurLinearOutlined';
import LineAxisOutlinedIcon from '@mui/icons-material/LineAxisOutlined';
import SpaOutlinedIcon from '@mui/icons-material/SpaOutlined';
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';

import heartFilled from '../../../images/heartFilled.svg';
import heartUnfilled from '../../../images/heartUnfilled.svg';

import styles from './InstaBookConfirmationPage.module.scss';

type FeedbackKind =
  | 'confirmSuccess'
  | 'rejectSuccess'
  | 'confirmError'
  | 'rejectError';

const calculateTravelAllowance = (distanceInKm: number): number => {
  if (distanceInKm > 21 && distanceInKm <= 46) return 7.5;
  if (distanceInKm > 46 && distanceInKm <= 76) return 15;
  if (distanceInKm > 76 && distanceInKm <= 91) return 25;
  if (distanceInKm > 91) return 30;
  return 0;
};

const getOfficeStatusCopy = (status?: number): string | null => {
  if (status === 1) return 'This is a good office to temp at.';
  if (status === 2) return 'One of the best offices to temp at.';
  return null;
};

const getInitialDistanceKm = (job?: JobProps): number | null => {
  const matrixDistance = Number(job?.distanceObj?.distance?.value);
  if (Number.isFinite(matrixDistance) && matrixDistance > 0) {
    return matrixDistance / 1000;
  }

  const rawDistance = Number(job?.distance);
  if (
    Number.isFinite(rawDistance) &&
    rawDistance >= 0 &&
    job?.location?.country === COUNTRY.CA
  ) {
    return rawDistance;
  }

  return null;
};

const InstaBookConfirmationPage: React.FC = () => {
  const history = useHistory();

  const locationState = useMemo(
    () => history.location.state as any,
    [history.location.state]
  );

  const { jobId, job: jobData } = locationState || {};
  const [job, setJob] = useState<JobProps>(jobData || ({} as JobProps));
  const [isFavouriteDentist, setFavouriteDentist] = useState<boolean>(
    !!jobData?.isFavouriteDentist
  );
  const [updatingFavourite, setUpdatingFavourite] = useState<boolean>(false);
  const [confirmOpen, setConfirmOpen] = useState<boolean>(false);
  const [rejectOpen, setRejectOpen] = useState<boolean>(false);
  const [feedbackKind, setFeedbackKind] = useState<FeedbackKind | null>(null);
  const [membershipStatus, setMembershipStatus] = useState<MembershipStatusResponse | null>(null);
  const [membershipLoading, setMembershipLoading] = useState<boolean>(false);
  const [membershipRestrictionDialog, setMembershipRestrictionDialog] = useState<MembershipDialogCopy | null>(null);
  const [distanceKm, setDistanceKm] = useState<number | null>(() =>
    getInitialDistanceKm(jobData)
  );
  const [remainingMinutes, setRemainingMinutes] = useState<number | null>(null);

  const hasFetchedFavourite = useRef<number | null>(null);

  const [hygienist] = usePersistedState<HygienistProps>({
    entityName: entities.hygienist,
  });

  const handleJobDetailsSuccess = useCallback((jobObj: JobProps) => {
    if (!_.isEmpty(jobObj)) {
      setJob(jobObj);
    }
  }, []);

  const handleFavouriteSuccess = useCallback((data: any) => {
    setFavouriteDentist(Boolean(data?.isFavouriteDentist));
  }, []);

  const handleDistanceMatrixSuccess = useCallback((data: { distance?: { value: number } }) => {
    const nextDistance = Number(data?.distance?.value);
    if (Number.isFinite(nextDistance) && nextDistance > 0) {
      setDistanceKm(nextDistance / 1000);
    }
  }, []);

  const handleConfirmSuccess = useCallback(() => {
    setFeedbackKind('confirmSuccess');
  }, []);

  const handleConfirmError = useCallback((error: unknown) => {
    const membershipCopy = getMembershipRestrictionDialogCopy(error);
    if (membershipCopy) {
      setMembershipRestrictionDialog(membershipCopy);
      return;
    }
    setFeedbackKind('confirmError');
  }, []);

  const handleRejectSuccess = useCallback(() => {
    setFeedbackKind('rejectSuccess');
  }, []);

  const handleRejectError = useCallback(() => {
    setFeedbackKind('rejectError');
  }, []);

  const [getJobDetailsLoading, , getJobDetails] = useActionApi<number, JobProps>({
    apiMethod: Api.getJobDetails,
    onSuccess: handleJobDetailsSuccess,
  });

  const [checkFavouriteLocationLoading, , checkFavouriteLocation] = useActionApi<number, any>({
    apiMethod: Api.checkFavouriteLocation,
    onSuccess: handleFavouriteSuccess,
  });

  const [getDistanceMatrixLoading, , getDistanceMatrix] = useActionApi<
    { lat: number; lng: number },
    { distance?: { value: number } }
  >({
    apiMethod: Api.getDistanceMatrix,
    onSuccess: handleDistanceMatrixSuccess,
  });

  const [confirmInstabookLoading, , confirmInstabook] = useActionApi<
    { jobId: number },
    any
  >({
    apiMethod: Api.confirmHygienistInstabook,
    onSuccess: handleConfirmSuccess,
    onError: handleConfirmError,
  });

  const [rejectInstabookLoading, , rejectInstabook] = useActionApi<
    { jobId: number },
    any
  >({
    apiMethod: Api.rejectHygienistInstabook,
    onSuccess: handleRejectSuccess,
    onError: handleRejectError,
  });

  useEffect(() => {
    if (jobId) {
      getJobDetails(jobId);
    }
  }, [getJobDetails, jobId]);

  useEffect(() => {
    let isMounted = true;

    const loadMembershipStatus = async () => {
      setMembershipLoading(true);
      try {
        const membership = await Api.getMembershipStatus();
        if (isMounted) {
          setMembershipStatus(membership);
        }
      } catch {
        if (isMounted) {
          setMembershipStatus(null);
        }
      } finally {
        if (isMounted) {
          setMembershipLoading(false);
        }
      }
    };

    loadMembershipStatus();
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (job?.locationId && hasFetchedFavourite.current !== job.locationId) {
      hasFetchedFavourite.current = job.locationId;
      checkFavouriteLocation(job.locationId);
    }
  }, [checkFavouriteLocation, job?.locationId]);

  useEffect(() => {
    const nextMinutes = Number(job?.instabookTimeLeft);
    if (Number.isFinite(nextMinutes)) {
      setRemainingMinutes(Math.max(0, Math.round(nextMinutes)));
    } else {
      setRemainingMinutes(null);
    }
  }, [job?.instabookTimeLeft]);

  useEffect(() => {
    if (remainingMinutes === null || remainingMinutes <= 0) return undefined;

    const timeout = window.setTimeout(() => {
      setRemainingMinutes((prev) => {
        if (prev === null) return prev;
        return Math.max(0, prev - 1);
      });
    }, 60000);

    return () => {
      window.clearTimeout(timeout);
    };
  }, [remainingMinutes]);

  useEffect(() => {
    if (distanceKm !== null) return;
    if (!job?.id) return;

    const nextDistance = getInitialDistanceKm(job);
    if (nextDistance !== null) {
      setDistanceKm(nextDistance);
      return;
    }

    if (job?.location?.lat && job?.location?.lon) {
      getDistanceMatrix({
        lat: Number(job.location.lat),
        lng: Number(job.location.lon),
      });
    }
  }, [
    distanceKm,
    getDistanceMatrix,
    job,
    job?.id,
    job?.location?.lat,
    job?.location?.lon,
  ]);

  const myId = Number(hygienist?.id);
  const isAssignedToCurrentUser =
    myId > 0 && Number(job?.hygienistId) === myId;
  const isPendingInstabook =
    isAssignedToCurrentUser &&
    Number(job?.Autobook) === 1 &&
    Number(job?.status) === 2;
  const isConfirmedShift =
    isAssignedToCurrentUser && Number(job?.status) === 3;

  useEffect(() => {
    if (job?.id && isConfirmedShift && !feedbackKind) {
      history.replace({
        pathname: 'confirmedOffer',
        state: { jobId: job.id },
      });
    }
  }, [feedbackKind, history, isConfirmedShift, job?.id]);

  const isJobReady = Boolean(job?.id && job?.location && job?.shifts?.length);
  const isLoading =
    getJobDetailsLoading ||
    checkFavouriteLocationLoading ||
    getDistanceMatrixLoading ||
    membershipLoading ||
    confirmInstabookLoading ||
    rejectInstabookLoading;

  const formattedShiftDate = useMemo(() => {
    if (!job?.shifts?.[0]?.shiftDate) return '';
    return dayjs(job.shifts[0].shiftDate).format('dddd, MMM D');
  }, [job?.shifts]);

  const shiftTime = useMemo(() => {
    if (!job?.shifts?.[0]) return '';
    return `${job.shifts[0].postedStart} - ${job.shifts[0].postedEnd}`;
  }, [job?.shifts]);

  const travelAllowance = useMemo(() => {
    if (distanceKm === null) return null;
    return calculateTravelAllowance(distanceKm);
  }, [distanceKm]);

  const paymentTermsText = useMemo(() => {
    return getPaymentTerms(job?.paymentTerms);
  }, [job?.paymentTerms]);

  const workingRelationshipText = useMemo(() => {
    if (job?.location?.country !== COUNTRY.US || typeof job?.paysW2 !== 'number') {
      return null;
    }
    return getPaymentType(job.paysW2);
  }, [job?.location?.country, job?.paysW2]);

  const officeStatusCopy = useMemo(() => {
    return getOfficeStatusCopy(job?.location?.status);
  }, [job?.location?.status]);
  const requesterMembershipDecision = useMemo(
    () => membershipStatus ?? getJobRequesterMembershipDecision(job),
    [job, membershipStatus]
  );
  const membershipBanner = membershipStatus
    ? getMembershipBannerCopy(membershipStatus, { firstName: hygienist?.firstName })
    : null;
  const membershipBlocksAutoBook = !membershipLoading && (
    !requesterMembershipDecision ||
    !canConfirmAutobookByMembership(requesterMembershipDecision)
  );
  const hasDetailedMembershipNotice = Boolean(
    membershipStatus && shouldShowMembershipBanner(membershipStatus) && membershipBanner
  );

  const hasMapCoordinates = Boolean(
    hygienist?.lat &&
    hygienist?.lon &&
    job?.location?.lat &&
    job?.location?.lon
  );

  const countdownText = useMemo(() => {
    if (remainingMinutes === null) {
      return 'This Auto-book will auto-confirm shortly if you do nothing.';
    }

    if (remainingMinutes <= 0) {
      return 'This Auto-book will auto-confirm shortly if you do nothing.';
    }

    return `This Auto-book will be automatically confirmed in ${remainingMinutes} minute${remainingMinutes === 1 ? '' : 's'} if you do nothing.`;
  }, [remainingMinutes]);

  const feedbackDialogConfig = useMemo(() => {
    if (!feedbackKind) return null;

    switch (feedbackKind) {
      case 'confirmSuccess':
        return {
          title: 'Auto-book Confirmed',
          content: `Nicely done${hygienist?.firstName ? `, ${hygienist.firstName}` : ''}! Your Auto-book shift is booked and confirmed.`,
          actions: [
            {
              label: 'View booked shift',
              variant: 'contained' as const,
              onClick: () => {
                setFeedbackKind(null);
                history.push({
                  pathname: 'confirmedOffer',
                  state: { jobId: job.id },
                });
              },
            },
            {
              label: 'Back home',
              variant: 'outlined' as const,
              onClick: () => {
                setFeedbackKind(null);
                history.push({ pathname: 'hygienistHome' });
              },
            },
          ],
        };
      case 'rejectSuccess':
        return {
          title: 'Auto-book Request Rejected',
          content: 'We have notified the office that you are not available for this shift.',
          actions: [
            {
              label: 'Back home',
              variant: 'contained' as const,
              onClick: () => {
                setFeedbackKind(null);
                history.push({ pathname: 'hygienistHome' });
              },
            },
          ],
        };
      case 'confirmError':
        return {
          title: 'Could Not Confirm Auto-book',
          content: 'Please try again. If this keeps happening, contact support.',
          actions: [
            {
              label: 'OK',
              variant: 'contained' as const,
              onClick: () => setFeedbackKind(null),
            },
          ],
        };
      case 'rejectError':
        return {
          title: 'Could Not Reject Auto-book',
          content: 'Please try again. If this keeps happening, contact support.',
          actions: [
            {
              label: 'OK',
              variant: 'contained' as const,
              onClick: () => setFeedbackKind(null),
            },
          ],
        };
      default:
        return null;
    }
  }, [feedbackKind, history, hygienist?.firstName, job.id]);

  const handleBack = useCallback(() => {
    history.push({ pathname: 'hygienistHome' });
  }, [history]);

  const handleToggleFavourite = useCallback(async () => {
    if (!job?.locationId || !job?.dentistId) return;

    setUpdatingFavourite(true);
    try {
      if (isFavouriteDentist) {
        await Api.removeFavouriteDentistByLocationId(job.locationId);
      } else {
        await Api.setFavouriteDentist(job.dentistId, job.locationId);
      }
      await checkFavouriteLocation(job.locationId);
    } finally {
      setUpdatingFavourite(false);
    }
  }, [checkFavouriteLocation, isFavouriteDentist, job?.dentistId, job?.locationId]);

  const handleViewMap = useCallback(() => {
    const hygienistAddress = `${hygienist?.lat},+${hygienist?.lon}`;
    const officeAddress = `${job?.location?.lat},+${job?.location?.lon}`;
    const url = `https://www.google.com/maps/dir/${hygienistAddress}/${officeAddress}`;

    try {
      // @ts-ignore
      if (window.cordova) {
        // @ts-ignore
        cordova.InAppBrowser.open(url, '_system', 'location=yes');
      } else {
        window.open(url, '_blank', 'location=yes');
      }
    } catch (error) {
      window.location.href = url;
    }
  }, [hygienist?.lat, hygienist?.lon, job?.location?.lat, job?.location?.lon]);

  const handleConfirmNow = useCallback(async () => {
    if (!job?.id) return;
    setConfirmOpen(false);
    await confirmInstabook({ jobId: job.id });
  }, [confirmInstabook, job?.id]);

  const handleRejectNow = useCallback(async () => {
    if (!job?.id) return;
    setRejectOpen(false);
    await rejectInstabook({ jobId: job.id });
  }, [job?.id, rejectInstabook]);

  if (!isJobReady && !isLoading) {
    return (
      <div className={styles.pageContainer}>
        <TopBarRedesigned onBack={handleBack} title="Auto-book Request" />
        <main className={styles.mainContent}>
          <div className={styles.unavailableCard}>
            <h2 className={styles.unavailableTitle}>This Auto-book request is unavailable.</h2>
            <p className={styles.unavailableText}>
              We could not load the details for this request. Please go back to your home calendar and try again.
            </p>
            <div className={styles.unavailableActions}>
              <StyledButton
                variant="contained"
                color="primary"
                className="no-fastclick"
                onClick={handleBack}
              >
                Back to Home
              </StyledButton>
            </div>
          </div>
        </main>
      </div>
    );
  }

  const requestUnavailable = isJobReady && !isPendingInstabook && !isConfirmedShift;

  return (
    <div className={styles.pageContainer}>
      <TopBarRedesigned onBack={handleBack} title="Auto-book Request" />

      <main className={styles.mainContent}>
        {requestUnavailable ? (
          <div className={styles.unavailableCard}>
            <h2 className={styles.unavailableTitle}>This Auto-book request is no longer pending.</h2>
            <p className={styles.unavailableText}>
              The status of this shift changed before you opened it. Go back to your calendar to see the latest state.
            </p>
            <div className={styles.unavailableActions}>
              <StyledButton
                variant="contained"
                color="primary"
                className="no-fastclick"
                onClick={handleBack}
              >
                Back to Home
              </StyledButton>
            </div>
          </div>
        ) : (
          <>
            <section className={styles.statusCard}>
              <div className={styles.statusIcon}>
                <FlashOnOutlinedIcon />
              </div>
              <div className={styles.statusContent}>
                <div className={styles.statusEyebrow}>Pending office request</div>
                <h1 className={styles.statusTitle}>You have an Auto-book request for this shift.</h1>
                <p className={styles.statusText}>
                  {countdownText}
                </p>
              </div>
              <div className={styles.statusMeta}>
                <div className={styles.countdownChip}>
                  <TimelapseOutlinedIcon />
                  {remainingMinutes !== null && remainingMinutes > 0
                    ? `${remainingMinutes} min remaining`
                    : 'Auto-confirming soon'}
                </div>
              </div>
            </section>

            <section className={styles.shiftCard}>
              <div className={styles.cardHeader}>
                <div>
                  <h2 className={styles.practiceName}>{job.location?.practiceName}</h2>
                  <p className={styles.practiceAddress}>
                    {job.location?.address}, {job.location?.city}, {job.location?.province} {job.location?.postalCode}
                  </p>
                </div>

                <button
                  className={styles.favouriteButton}
                  onClick={handleToggleFavourite}
                  disabled={updatingFavourite}
                  aria-label={isFavouriteDentist ? 'Remove favourite office' : 'Add favourite office'}
                >
                  <img
                    src={isFavouriteDentist ? heartFilled : heartUnfilled}
                    alt=""
                    className={styles.favouriteIcon}
                  />
                </button>
              </div>

              {officeStatusCopy ? (
                <div className={styles.officeStatusCard}>
                  <CheckCircleOutlineOutlinedIcon />
                  <div>
                    <div className={styles.officeStatusLabel}>
                      {job.location?.status === 2 ? 'Elite office' : 'Pro office'}
                    </div>
                    <div className={styles.officeStatusText}>{officeStatusCopy}</div>
                  </div>
                </div>
              ) : null}

              <div className={styles.summaryGrid}>
                <div className={styles.summaryItem}>
                  <div className={styles.summaryLabel}>
                    <CalendarTodayOutlinedIcon />
                    Shift Date
                  </div>
                  <div className={styles.summaryValue}>{formattedShiftDate}</div>
                </div>

                <div className={styles.summaryItem}>
                  <div className={styles.summaryLabel}>
                    <AccessTimeOutlinedIcon />
                    Shift Time
                  </div>
                  <div className={styles.summaryValue}>{shiftTime}</div>
                </div>

                <div className={styles.summaryItem}>
                  <div className={styles.summaryLabel}>
                    <AttachMoneyOutlinedIcon />
                    Hourly Rate
                  </div>
                  <div className={styles.summaryValue}>${job.hourlyRate}/hr</div>
                </div>

                <div className={styles.summaryItem}>
                  <div className={styles.summaryLabel}>
                    <FlashOnOutlinedIcon />
                    Payment Terms
                  </div>
                  <div className={styles.summaryValue}>{paymentTermsText}</div>
                </div>
              </div>

              {workingRelationshipText ? (
                <div className={styles.secondaryRow}>
                  Working relationship: {workingRelationshipText}
                </div>
              ) : null}

              {travelAllowance !== null ? (
                <div className={styles.travelAllowanceCard}>
                  <div className={styles.travelAllowanceTitle}>Travel allowance for this shift</div>
                  <div className={styles.travelAllowanceValue}>${travelAllowance.toFixed(2)}</div>
                  {distanceKm !== null ? (
                    <div className={styles.travelAllowanceMeta}>
                      Calculated from about {distanceKm.toFixed(1)} km of travel.
                    </div>
                  ) : null}
                </div>
              ) : null}

              <div className={styles.actionRow}>
                <button
                  className={styles.mapButton}
                  onClick={handleViewMap}
                  disabled={!hasMapCoordinates}
                >
                  <RoomOutlinedIcon />
                  View Map
                </button>
              </div>
            </section>

            <section className={styles.infoCard}>
              <h3 className={styles.infoTitle}>How this request works</h3>
              <p className={styles.infoText}>
                You can wait and let this request auto-confirm, or you can confirm it right now.
              </p>
              <p className={styles.infoText}>
                If you reject it, we will notify {job.location?.practiceName} that you are not available for this shift.
              </p>
              <p className={styles.infoText}>
                To keep Auto-book active, make sure your daily availability reflects when you are truly available.
              </p>
            </section>

            {membershipStatus && shouldShowMembershipBanner(membershipStatus) && membershipBanner ? (
              <section className={styles.infoCard}>
                <h3 className={styles.infoTitle}>{membershipBanner.title}</h3>
                <p className={styles.infoText}>
                  <MembershipBannerMessage copy={membershipBanner} />
                </p>
                {membershipBlocksAutoBook ? (
                  <p className={styles.infoText}>
                    This Auto-book request cannot be confirmed while this membership state is active.
                  </p>
                ) : null}
                <StyledButton
                  variant="outlined"
                  color="primary"
                  className="no-fastclick"
                  onClick={() => openMembershipPath(history as any, membershipStatus.nextAction?.ctaPath || 'myStats')}
                >
                  {membershipStatus.nextAction?.ctaLabel || 'View My Stats'}
                </StyledButton>
              </section>
            ) : null}

            {membershipBlocksAutoBook && !hasDetailedMembershipNotice ? (
              <section className={styles.infoCard}>
                <h3 className={styles.infoTitle}>
                  {requesterMembershipDecision ? 'Auto-book unavailable' : 'Membership access unavailable'}
                </h3>
                <p className={styles.infoText}>
                  {requesterMembershipDecision
                    ? 'This Auto-book request cannot be confirmed with your current membership access.'
                    : 'We could not verify your membership access right now. Please try again before confirming this Auto-book request.'}
                </p>
              </section>
            ) : null}

            <section className={styles.buttonGroup}>
              <StyledButton
                variant="contained"
                color="primary"
                className={`no-fastclick ${styles.confirmButton}`}
                onClick={() => setConfirmOpen(true)}
                disabled={isLoading || membershipBlocksAutoBook}
              >
                Confirm Auto-book Now
              </StyledButton>

              <StyledButton
                variant="outlined"
                color="primary"
                className={`no-fastclick ${styles.rejectButton}`}
                onClick={() => setRejectOpen(true)}
                disabled={isLoading}
              >
                Reject This Auto-book
              </StyledButton>
            </section>

            <section className={styles.detailsGrid}>
              <div className={styles.detailItem}>
                <div className={styles.detailIcon}><PersonOutlineOutlinedIcon /></div>
                <div>
                  <div className={styles.detailLabel}>Primary Contact</div>
                  <div className={styles.detailValue}>{job.location?.primaryContact || '-'}</div>
                </div>
              </div>

              <div className={styles.detailItem}>
                <div className={styles.detailIcon}><TimeToLeaveOutlinedIcon /></div>
                <div>
                  <div className={styles.detailLabel}>Parking</div>
                  <div className={styles.detailValue}>{getTalentParkingLabel(job.location?.parking) || '-'}</div>
                </div>
              </div>

              <div className={styles.detailItem}>
                <div className={styles.detailIcon}><BlurLinearOutlinedIcon /></div>
                <div>
                  <div className={styles.detailLabel}>Radiography</div>
                  <div className={styles.detailValue}>{job.location?.radiography || '-'}</div>
                </div>
              </div>

              <div className={styles.detailItem}>
                <div className={styles.detailIcon}><WavesOutlinedIcon /></div>
                <div>
                  <div className={styles.detailLabel}>Ultrasonic</div>
                  <div className={styles.detailValue}>{job.location?.ultrasonic || '-'}</div>
                </div>
              </div>

              <div className={styles.detailItem}>
                <div className={styles.detailIcon}><TimelapseOutlinedIcon /></div>
                <div>
                  <div className={styles.detailLabel}>Avg Recall</div>
                  <div className={styles.detailValue}>{job.location?.avgApptTime || '-'}</div>
                </div>
              </div>

              <div className={styles.detailItem}>
                <div className={styles.detailIcon}><LineAxisOutlinedIcon /></div>
                <div>
                  <div className={styles.detailLabel}>Charting</div>
                  <div className={styles.detailValue}>{job.location?.charting || '-'}</div>
                </div>
              </div>

              <div className={styles.detailItem}>
                <div className={styles.detailIcon}><DesktopMacOutlinedIcon /></div>
                <div>
                  <div className={styles.detailLabel}>Software</div>
                  <div className={styles.detailValue}>{job.location?.software || '-'}</div>
                </div>
              </div>

              <div className={styles.detailItem}>
                <div className={styles.detailIcon}><DiningOutlinedIcon /></div>
                <div>
                  <div className={styles.detailLabel}>Lunch Break</div>
                  <div className={styles.detailValue}>Unpaid</div>
                </div>
              </div>

              <div className={styles.detailItem}>
                <div className={styles.detailIcon}><SpaOutlinedIcon /></div>
                <div>
                  <div className={styles.detailLabel}>Scent Policy</div>
                  <div className={`${styles.detailValue} ${job.location?.scentFreePolicy !== 'scent-free' ? styles.warning : ''}`}>
                    {job.location?.scentFreePolicy === 'scent-free' ? 'Scent-Free' : 'Not Scent-Free'}
                  </div>
                </div>
              </div>
            </section>

            {job.shiftNotes && job.shiftNotes.trim() ? (
              <ShiftNotesPanel notes={job.shiftNotes} />
            ) : null}
          </>
        )}
      </main>

      <ConfirmDialog
        open={confirmOpen}
        title="Confirm Auto-booking?"
        dataTestId="autobook-confirm-dialog"
        contentDesc="autobook-confirm-dialog"
        content={
          <div className={styles.dialogBody}>
            <p>You are confirming the following shift:</p>
            <p><strong>{job.location?.practiceName}</strong></p>
            <p>{formattedShiftDate}</p>
            <p>{shiftTime}</p>
            <p>${job.hourlyRate}/hr</p>
          </div>
        }
        actions={[
          { label: 'Go back', variant: 'outlined', onClick: () => setConfirmOpen(false) },
          { label: 'Confirm', variant: 'contained', onClick: handleConfirmNow, disabled: isLoading || membershipBlocksAutoBook },
        ]}
        onClose={() => setConfirmOpen(false)}
      />

      <ConfirmDialog
        open={rejectOpen}
        title="Reject this Auto-book request?"
        dataTestId="autobook-reject-dialog"
        contentDesc="autobook-reject-dialog"
        content={
          <div className={styles.dialogBody}>
            <p>
              We will notify {job.location?.practiceName} that you are not available for this shift.
            </p>
            <p>
              To keep your Auto-book scheduling feature active, make sure your daily schedule stays up to date with your real availability.
            </p>
          </div>
        }
        actions={[
          { label: 'Go back', variant: 'outlined', onClick: () => setRejectOpen(false) },
          { label: 'Reject', variant: 'contained', onClick: handleRejectNow, disabled: isLoading },
        ]}
        onClose={() => setRejectOpen(false)}
      />

      <ConfirmDialog
        open={!!feedbackDialogConfig}
        title={feedbackDialogConfig?.title}
        dataTestId={`autobook-${feedbackKind || 'feedback'}-dialog`}
        contentDesc={`autobook-${feedbackKind || 'feedback'}-dialog`}
        content={feedbackDialogConfig?.content}
        actions={feedbackDialogConfig?.actions}
        onClose={() => setFeedbackKind(null)}
      />

      <MembershipRestrictionModal
        isOpen={!!membershipRestrictionDialog}
        copy={membershipRestrictionDialog}
        onClose={() => setMembershipRestrictionDialog(null)}
      />

      <FixedLoader loading={isLoading} />
    </div>
  );
};

export default InstaBookConfirmationPage;
